// @ts-nocheck
import React, { useEffect, useMemo, useState } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import type { ResolvedLoanBook } from '@valutten/shared-schemas';
import { Scale } from 'lucide-react';
import PageHeader from '../../components/common/PageHeader';
import MonthPicker from '../../components/common/MonthPicker';
import { resolveLoanBookSelection, getDisplayLoanBooks } from '../../utils/loanBookResolution';
import { useBrokerStatuses } from '../../hooks/useBrokerStatuses';
import {
  useApprovedVsSettledVariance,
  type VarianceLoanRow,
  type VarianceGroupBy,
} from '../../hooks/reports/useApprovedVsSettledVariance';
import { resolvePeriod, type PeriodKey } from './approvedVsSettledPeriod';
import DataAvailabilityBanner from '../../components/reports/DataAvailabilityBanner';
import VarianceKpiStrip from '../../components/reports/VarianceKpiStrip';
import VarianceByDimensionTable from '../../components/reports/VarianceByDimensionTable';
import VarianceLoanTable from '../../components/reports/VarianceLoanTable';
import VarianceDetailModal from '../../components/reports/VarianceDetailModal';

interface Office {
  id: string;
  name: string;
}

interface OutletContextType {
  userData: any;
  companyData: any;
  userVisibleOffices: Office[];
  userVisibleTemplates: any[];
  loanBooks: ResolvedLoanBook[];
}

// Loan-book name fallbacks for prod entries whose Firestore docs predate the `name` field
// convention. Subscribers think in loan books / entities — never in templates.
const LOAN_BOOK_NAME_FALLBACKS: Record<string, string> = {
  'RISjXTNkYREXdzFwN2L7': 'Loan Market',
  'e6PK4ikHYEhUqnhu2UdL': 'Entossey',
  'mCeqUyuWfGWZmDUbu1YB': 'Entical',
  'Fa3vNzL234xK1DJuuXRP': 'Entover',
  'wlth_lend': 'WLTH',
  'orde_financial': 'Orde Financial',
};

const cleanName = (name: string) => name.replace(/\s*\(LMG\)\s*/gi, '').replace(/\s*Commission File\s*/gi, '').trim();

// Period selector options. Subscribers think in periods, not month-range pickers — these
// match the language used elsewhere in the platform (Settlement Age, Trail Drop-Off).
const PERIOD_OPTIONS: { value: PeriodKey; label: string }[] = [
  { value: 'this-month',     label: 'This month' },
  { value: 'last-month',     label: 'Last month' },
  { value: 'this-quarter',   label: 'This quarter' },
  { value: 'last-quarter',   label: 'Last quarter' },
  { value: 'ytd',            label: 'Year to date' },
  { value: 'last-12-months', label: 'Last 12 months' },
  { value: 'last-year',      label: 'Last year' },
  { value: 'custom',         label: 'Custom range…' },
];


/**
 * Approved vs Settled Variance — surfaces upfront commission left on the table when loans
 * settle below their originally-approved amount. Generic across all fileTemplates via the
 * dual-formula CTE in varianceMergeBuilder; templates without source data degrade gracefully
 * via the DataAvailabilityBanner rather than misleading $0 KPIs.
 */
const ApprovedVsSettledPage: React.FC = () => {
  const { userData, userVisibleOffices = [], userVisibleTemplates = [], loanBooks = [] } =
    useOutletContext<OutletContextType>();
  const [searchParams, setSearchParams] = useSearchParams();

  // ── Filter state, URL-persisted ─────────────────────────────────────────
  const [period, setPeriod] = useState<PeriodKey>(
    (searchParams.get('period') as PeriodKey) || 'last-12-months',
  );
  const [customStart, setCustomStart] = useState<string>(searchParams.get('start') || '');
  const [customEnd, setCustomEnd] = useState<string>(searchParams.get('end') || '');
  const [selectedOffice, setSelectedOffice] = useState<string>(searchParams.get('office') || 'all');
  const [selectedTemplate, setSelectedTemplate] = useState<string>(searchParams.get('loanBook') || 'all');
  const [selectedBroker, setSelectedBroker] = useState<string>(searchParams.get('broker') || 'all');
  const [decreasedOnly, setDecreasedOnly] = useState<boolean>(searchParams.get('decreasedOnly') !== 'false');
  const [excludeConstructionLikely, setExcludeConstructionLikely] = useState<boolean>(
    searchParams.get('hideConstruction') !== 'false',
  );

  // Brokers list — fetched from getDistinctBrokers (returns every broker_display ever seen
  // in raw_commissions, including historical / Asset-Transfer / inactive variants), then
  // filtered to ACTIVE brokers only via useBrokerStatuses (Firestore brokers subcollection).
  // Anything not in the active set (e.g. "Andrew Timms (Asset Tfr) (Asset Tfr)" historical
  // variants) is dropped — subscribers don't want to filter by archived brokers.
  const [allDistinctBrokers, setAllDistinctBrokers] = useState<string[]>([]);
  useEffect(() => {
    if (!userData?.companyId) return;
    const callable = httpsCallable<{ companyId: string }, string[]>(functions, 'getDistinctBrokers');
    callable({ companyId: userData.companyId })
      .then(res => setAllDistinctBrokers((res.data || []).slice().sort()))
      .catch(err => { console.error('[ApprovedVsSettledPage] getDistinctBrokers failed:', err); setAllDistinctBrokers([]); });
  }, [userData?.companyId]);

  const { brokerStatuses } = useBrokerStatuses(userData?.companyId, selectedOffice);
  const brokerOptions = useMemo(() => {
    if (brokerStatuses.size === 0) return allDistinctBrokers; // statuses still loading — show all
    return allDistinctBrokers.filter(name => brokerStatuses.get(name) === 'active');
  }, [allDistinctBrokers, brokerStatuses]);

  // Selected loan for the detail modal.
  const [selectedLoan, setSelectedLoan] = useState<VarianceLoanRow | null>(null);

  // Persist filter state into the URL so refresh / share keeps the view intact.
  const updateUrl = (next: Record<string, string | boolean | number | null>) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(next).forEach(([k, v]) => {
      if (v == null || v === '' || v === 'all') params.delete(k);
      else params.set(k, String(v));
    });
    setSearchParams(params, { replace: true });
  };

  // ── Resolve loan-book selection → templateId / entityCode ───────────────
  const resolvedParams = useMemo(
    () => resolveLoanBookSelection(selectedTemplate, loanBooks),
    [selectedTemplate, loanBooks],
  );

  // ── Resolve period to month bounds + display label ──────────────────────
  const { startMonth, endMonth, label: periodLabel } = useMemo(
    () => resolvePeriod(period, customStart, customEnd),
    [period, customStart, customEnd],
  );

  // ── Auto-derive groupBy from filter context — never asked of the user.
  // Broker selected → degenerate breakdown (1 row); we hide the table downstream.
  // Office selected → group brokers within office.
  // Loan book selected → group offices within loan book.
  // Otherwise → group by loan book (the natural top-level dimension and the only one that
  // always renders meaningful labels for both unified-mode AND legacy companies. Entity
  // grouping was tried first but legacy companies (e.g. Entourage in PROD) have NULL
  // entity_code which renders as "(unattributed)" — terrible UX. Loan book is safe for both.).
  const autoGroupBy: VarianceGroupBy = useMemo(() => {
    if (selectedBroker !== 'all') return 'broker';
    if (selectedOffice !== 'all') return 'broker';
    if (selectedTemplate !== 'all') return 'office';
    return 'template';
  }, [selectedBroker, selectedOffice, selectedTemplate]);

  const showBreakdown = selectedBroker === 'all';

  // ── Hook ────────────────────────────────────────────────────────────────
  const { data, loading, error, refetch } = useApprovedVsSettledVariance({
    companyId: userData?.companyId,
    startMonth,
    endMonth,
    officeId: selectedOffice !== 'all' ? selectedOffice : undefined,
    templateId: resolvedParams.templateId,
    entityCode: resolvedParams.entityCode,
    brokerId: selectedBroker !== 'all' ? selectedBroker : undefined,
    groupBy: autoGroupBy,
    decreasedOnly,
    excludeConstructionLikely,
    loanLimit: 5000,
  });

  // ── Loan-book name lookup for the banner / breakdown labels ─────────────
  const loanBookNames = useMemo(() => {
    const out: Record<string, string> = { ...LOAN_BOOK_NAME_FALLBACKS };
    loanBooks.forEach(lb => { if (lb?.id) out[lb.id] = lb.name || lb.id; });
    userVisibleTemplates.forEach(t => {
      if (t?.id && !out[t.id]) out[t.id] = cleanName(t.name || t.templateName || t.id);
    });
    return out;
  }, [loanBooks, userVisibleTemplates]);

  // Office name lookup for the dimension breakdown table when groupBy=office.
  const dimensionLabelLookup = useMemo(() => {
    if (autoGroupBy === 'office') {
      const out: Record<string, string> = {};
      userVisibleOffices.forEach(o => { if (o.id) out[o.id] = o.name; });
      return out;
    }
    if (autoGroupBy === 'template' || autoGroupBy === 'entity') return loanBookNames;
    return {};
  }, [autoGroupBy, userVisibleOffices, loanBookNames]);

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      <PageHeader
        icon={Scale}
        title="Approved vs Settled Variance"
        subtitle="Where loans settled below approved amount — and the upfront commission left on the table"
        onRefresh={refetch}
        isRefreshing={loading}
      />

      {/* Filter card */}
      <div className="bg-gray-800 rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          <FilterSelect
            label="Period"
            value={period}
            onChange={v => { setPeriod(v as PeriodKey); updateUrl({ period: v }); }}
            options={PERIOD_OPTIONS}
          />
          <FilterSelect
            label="Office"
            value={selectedOffice}
            onChange={v => { setSelectedOffice(v); updateUrl({ office: v }); }}
            options={[{ value: 'all', label: 'All offices' }, ...userVisibleOffices.map(o => ({ value: o.id, label: o.name }))]}
          />
          <FilterSelect
            label="Loan book"
            value={selectedTemplate}
            onChange={v => { setSelectedTemplate(v); updateUrl({ loanBook: v }); }}
            options={(() => {
              // Unified-mode tenants (VFA, Timms): collapse loan_books by entityCode so
              // multiple loan_books that share an entity (e.g. Choice + LMG → LOAN_MARKET)
              // render as ONE option. Suppress the fileTemplates fallback in this mode —
              // the entity-grouped options already cover every active template, and
              // adding template-level entries would duplicate the dropdown (the bug
              // observed on VFA stage 2026-05-01: 6 entries instead of 2).
              const hasEntityLoanBooks = loanBooks.some(lb => lb.type === 'entity' && lb.entityCode);
              const loanBookEntries = getDisplayLoanBooks(loanBooks);
              // Legacy tenants (Entourage pre-migration): show fileTemplates as the
              // dropdown source if no loan_books exist OR if loan_books exist but are
              // template-typed (legacy seed). Templates not already represented are appended.
              const fileTemplateEntries = hasEntityLoanBooks
                ? []
                : userVisibleTemplates
                    .filter(t => t?.id && !loanBooks.some(lb => lb.templateId === t.id || lb.id === t.id))
                    .map(t => ({ value: t.id, label: cleanName(t.name || t.templateName || t.id) }));
              return [
                { value: 'all', label: 'All loan books' },
                ...loanBookEntries,
                ...fileTemplateEntries,
              ];
            })()}
          />
          <FilterSelect
            label="Broker"
            value={selectedBroker}
            onChange={v => { setSelectedBroker(v); updateUrl({ broker: v }); }}
            options={[
              { value: 'all', label: 'All brokers' },
              ...brokerOptions.map(b => ({ value: b, label: b })),
            ]}
          />
          <div className="flex flex-col">
            <span className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">Mode</span>
            <div className="flex flex-col gap-2 mt-1">
              <label className="inline-flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={decreasedOnly}
                  onChange={(e) => { setDecreasedOnly(e.target.checked); updateUrl({ decreasedOnly: e.target.checked ? null : 'false' }); }}
                />
                Decreased only
              </label>
              <label className="inline-flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={excludeConstructionLikely}
                  onChange={(e) => { setExcludeConstructionLikely(e.target.checked); updateUrl({ hideConstruction: e.target.checked ? null : 'false' }); }}
                />
                Hide construction-likely
              </label>
            </div>
          </div>
        </div>

        {/* Custom date range — only shown when Period === 'custom'. Uses the dark-themed
            MonthPicker added across the app instead of the white browser-default <input type="month">. */}
        {period === 'custom' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3 pt-3 border-t border-gray-700/50">
            <div className="flex flex-col">
              <label className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">From month</label>
              <MonthPicker
                value={customStart}
                onChange={(v) => { setCustomStart(v); updateUrl({ start: v }); }}
                placeholder="Select month"
                maxMonth={customEnd || undefined}
              />
            </div>
            <div className="flex flex-col">
              <label className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">To month</label>
              <MonthPicker
                value={customEnd}
                onChange={(v) => { setCustomEnd(v); updateUrl({ end: v }); }}
                placeholder="Select month"
                minMonth={customStart || undefined}
              />
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Data availability — renders only when some loan books lack signal */}
      {data && (
        <DataAvailabilityBanner
          perTemplate={data.dataAvailability.perTemplate}
          overallSupportsVariance={data.dataAvailability.overallSupportsVariance}
          loanBookNames={loanBookNames}
        />
      )}

      {/* Empty state — when the period contains no upfront commission rows AT ALL (vs the
          banner above which fires when rows exist but none have approved-amount data).
          Without this, periods like "This quarter" with no settled loans yet render the
          page completely blank — looks broken. */}
      {data
        && !data.dataAvailability.overallSupportsVariance
        && data.dataAvailability.perTemplate.length === 0 && (
        <div className="bg-gray-800 rounded-lg p-8 text-center">
          <p className="text-gray-300 text-sm font-medium">No upfront commissions in this period yet.</p>
          <p className="text-gray-500 text-xs mt-1">
            {periodLabel} — try a wider window like "Last 12 months".
          </p>
        </div>
      )}

      {/* When NO loan books support variance, hide the analytical sections entirely. */}
      {data && data.dataAvailability.overallSupportsVariance && (
        <>
          <VarianceKpiStrip
            kpis={data.kpis}
            loading={loading}
            periodLabel={periodLabel}
            subsequentUpfrontMode={data.subsequentUpfrontMode}
          />

          {showBreakdown && (
            <div>
              <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-3">
                Breakdown by {(autoGroupBy === 'template' || autoGroupBy === 'entity') ? 'loan book' : autoGroupBy}
              </h3>
              <VarianceByDimensionTable
                rows={data.byDimension}
                groupBy={autoGroupBy}
                labelLookup={dimensionLabelLookup}
              />
            </div>
          )}

          <div>
            <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-3">
              Per-loan drill-down ({data.loans.length} {data.loans.length === 1 ? 'loan' : 'loans'}, sorted by decrease)
            </h3>
            <VarianceLoanTable
              rows={data.loans}
              loading={loading}
              onRowClick={(loan) => setSelectedLoan(loan)}
              showSubsequent={data.subsequentUpfrontMode !== 'off'}
            />
          </div>
        </>
      )}

      {/* Loading skeleton when first request is in flight */}
      {loading && !data && (
        <div className="bg-gray-800 rounded-lg p-8 text-center">
          <div className="text-gray-400 text-sm">Loading variance data…</div>
        </div>
      )}

      {/* Detail / annotation modal */}
      {selectedLoan && userData?.companyId && (
        <VarianceDetailModal
          loan={selectedLoan}
          companyId={userData.companyId}
          onClose={() => setSelectedLoan(null)}
          onSaved={refetch}
        />
      )}
    </div>
  );
};

const FilterSelect: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}> = ({ label, value, onChange, options }) => (
  <div className="flex flex-col">
    <label className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">{label}</label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-blue-500 transition"
    >
      {options.map(o => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  </div>
);

export default ApprovedVsSettledPage;
